import { useEffect, useRef, useState } from 'react';
import {
  MIN_REASON_CHARS, REASON_SCOPES, isValidReason, setSessionGrant, type ReasonScope,
} from '@lib/useSessionReason';

/**
 * ReasonForAccessModal - the reason-for-access dialog, the Portal's (row 418, William 19 Sep 2026).
 *
 * LIFTED from the Portal Frontend, v1/src/components/trust-center/ReasonForAccessModal.tsx, onto
 * Care's paper CSS (.modal-scrim / .modal), with two differences said plainly:
 *
 *   1. NO /api/phi/intent. The Portal mints an intent id server-side; Care's gate is on every PHI
 *      route (a reason under 4 characters is a 400 and every read writes its own audit row), so the
 *      modal is the VISIBLE half only: it records the scope and the reason in the session store, and
 *      each call carries them. Nothing is minted; nothing is skipped.
 *   2. THE MINIMUM IS 4 CHARACTERS, the server's rule (members-list.ts lines 611 to 620 on the Portal,
 *      every Care lookup route here), as the spec states it. The Portal's own dialog asks for 10 in
 *      its copy; the wire accepts 4 on both stations, and one number is better than two.
 *
 * Two ways to use it, exactly as the Portal does:
 *   - the ENTRY gate: `mandatory`, `allowedScopes` = the scopes that open the page, the submit writes
 *     the session grant (setSessionGrant) and the page unlocks;
 *   - the EXPORT re-prompt: `onSubmitted` given, `defaultScope="export"`: the reason is handed BACK to
 *     the caller and the session grant is untouched, so an export always rides its own reason.
 */
interface Props {
  onClose: () => void;
  /** Default scope for this invocation; the caller can pre-select. */
  defaultScope?: ReasonScope;
  /** True when the section behind this modal is gated until a reason is logged. */
  mandatory?: boolean;
  /** When provided, the reason is handed back instead of written to the session grant. */
  onSubmitted?: (g: { scope: ReasonScope; reason: string; mintedAt: string }) => void;
  /** When provided, only these scopes are selectable (the entry gate passes the set that unlocks the page). */
  allowedScopes?: ReadonlyArray<ReasonScope> | undefined;
}

export function ReasonForAccessModal({ onClose, defaultScope = 'roster_slice', mandatory, onSubmitted, allowedScopes }: Props) {
  const options = REASON_SCOPES.filter((o) => !allowedScopes || allowedScopes.includes(o.value));
  const initial = options.some((o) => o.value === defaultScope) ? defaultScope : (options[0]?.value ?? 'roster_slice');
  const [scope, setScope] = useState<ReasonScope>(initial);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    boxRef.current?.focus();
  }, []);

  const submit = () => {
    if (!isValidReason(reason)) {
      setError(`Please provide a reason of at least ${MIN_REASON_CHARS} characters.`);
      return;
    }
    const mintedAt = new Date().toISOString();
    if (onSubmitted) onSubmitted({ scope, reason: reason.trim(), mintedAt });
    else setSessionGrant(scope, reason);
    onClose();
  };

  return (
    <div className="modal-scrim" role="presentation">
      <div className="modal tier-3" role="dialog" aria-modal="true" aria-labelledby="reason-modal-title">
        <header style={{ padding: '12px 16px', borderBottom: '1px solid var(--rule)' }}>
          <h3 id="reason-modal-title" style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Reason for PHI access</h3>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Per HIPAA 164.312(b), every PHI access requires a logged reason.
          </p>
          {mandatory && (
            <p className="small" style={{ margin: '4px 0 0', fontWeight: 500 }}>
              Access to this section is gated until you log a reason.
            </p>
          )}
        </header>
        <div style={{ padding: '14px 16px', display: 'grid', gap: 12 }}>
          <label className="small" style={{ display: 'grid', gap: 4, fontWeight: 600 }}>
            Scope
            <select
              className="select-input"
              aria-label="Scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as ReasonScope)}
              style={{ fontWeight: 400 }}
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="small" style={{ display: 'grid', gap: 4, fontWeight: 600 }}>
            Reason
            <textarea
              ref={boxRef}
              className="textarea-input"
              aria-label="Reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. member called about kit status"
              style={{ fontWeight: 400 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
              }}
            />
            <span className="micro muted" style={{ fontWeight: 400 }}>
              Minimum {MIN_REASON_CHARS} characters. This text is logged verbatim in the audit trail.
            </span>
          </label>
          {error && <p className="small" role="alert" style={{ margin: 0, color: 'var(--crimson)' }}>{error}</p>}
        </div>
        <footer style={{ padding: '12px 16px', borderTop: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            {mandatory ? 'Not now' : 'Cancel'}
          </button>
          <button type="button" className="btn btn-sm btn-primary" disabled={!reason.trim()} onClick={submit}>
            Submit &amp; log
          </button>
        </footer>
      </div>
    </div>
  );
}
